import { CrmHeader, CrmPagination, CrmContacts } from '../components/crm/CrmUi';
import { useCrmListState, useCrmNavigation } from '../hooks/useCrmNavigation';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useClients } from '@offertify/query';
import { canaliAPI } from '@offertify/api-client';

import { clientsAPI, ficAPI } from '../utils/api';
import { usePermissions } from '../hooks/usePermissions';
import toast from 'react-hot-toast';
import {
  Plus,
  Search,
  User,
  Building2,
  MapPin,
  Loader2,
  DownloadCloud,
  Globe,
  UserCog,
} from 'lucide-react';

function contrastColor(hex: string): string {
  const h = hex.replace('#', '');
  if (h.length !== 6) return '#000000';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 140 ? '#111827' : '#ffffff';
}

export default function Clients() {
  const queryClient = useQueryClient();
  const { openDetail, openCreate } = useCrmNavigation('/clients');
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('clienti:create');
  const canSync = hasPermission('clienti:sync');
  const { searchParams, page, updateParam } = useCrmListState();
  const search = searchParams.get('search') || '';
  const tipoFilter = searchParams.get('tipo') || '';
  const canaleIdsFilter = searchParams.get('canale_id')?.split(',').filter(Boolean) || [];
  const portalFilter = searchParams.get('portal') || '';
  // Fetch FIC status
  const { data: ficStatus } = useQuery({
    queryKey: ['fic-status'],
    queryFn: async () => {
      const response = await ficAPI.getStatus();
      return response.data;
    },
  });

  // Tenant's channel list (unpaginated), shared across the filter chips and
  // the create-client modal picker. Shares a cache key with ClientEditForm.
  const { data: canali } = useQuery({
    queryKey: ['canali-select'],
    queryFn: async () => {
      const res = await canaliAPI.getCanaliForSelect();
      return res.data;
    },
  });

  const toggleCanaleFilter = (id: string) => {
    const next = canaleIdsFilter.includes(id)
      ? canaleIdsFilter.filter((x) => x !== id)
      : [...canaleIdsFilter, id];
    updateParam({ canale_id: next });
  };

  // Apply all filters before pagination on the server.
  const { data: clientsData, isLoading, isError, refetch } = useClients({
    page,
    limit: 20,
    search: search || undefined,
    tipo: tipoFilter || undefined,
    canale_id: canaleIdsFilter.length ? canaleIdsFilter : undefined,
    has_portal_access: portalFilter === 'with' ? true : portalFilter === 'without' ? false : undefined,
  });

  // Import from FIC mutation
  const importMutation = useMutation({
    mutationFn: () => clientsAPI.importFromFIC(),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success(response.data.message);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nell\'importazione');
    },
  });

  const clients = clientsData?.data || [];
  const pagination = clientsData?.pagination;

  return (
    <div className="space-y-6">
      {/* Header */}
      <CrmHeader title="Clienti" description="Gestisci l’anagrafica clienti">
          {ficStatus?.connected && canSync && (
            <button
              onClick={() => importMutation.mutate()}
              disabled={importMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {importMutation.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <DownloadCloud className="h-5 w-5" />
              )}
              Importa da FIC
            </button>
          )}
          {canCreate && (
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-5 w-5" />
              Nuovo Cliente
            </button>
          )}
      </CrmHeader>

      {Array.from(searchParams.keys()).some(key => key !== 'page') && (
        <button onClick={() => updateParam(Object.fromEntries(Array.from(searchParams.keys()).map(key => [key, null])))} className="text-sm text-blue-600 hover:underline">Azzera filtri</button>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca per nome, email, P.IVA..."
              value={search}
              onChange={(e) => {
                updateParam({ search: e.target.value });
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={tipoFilter}
            onChange={(e) => {
              updateParam({ tipo: e.target.value });
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Tutti i tipi</option>
            <option value="persona_fisica">Persona Fisica</option>
            <option value="azienda">Azienda</option>
          </select>
          <select
            value={portalFilter}
            onChange={(e) => {
              updateParam({ portal: e.target.value });
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Accesso portale</option>
            <option value="with">Con accesso</option>
            <option value="without">Senza accesso</option>
          </select>
        </div>

        {canali && canali.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Canale:
            </span>
            {canali.map((c) => {
              const active = canaleIdsFilter.includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggleCanaleFilter(c.id)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    active
                      ? 'border-transparent'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                  style={active ? { backgroundColor: c.color, color: contrastColor(c.color) } : undefined}
                >
                  {c.nome}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Clients List */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        {isError ? (
          <div role="alert" className="p-8 text-center text-gray-600">
            <p>Impossibile caricare i risultati.</p>
            <button onClick={() => refetch()} className="mt-3 text-blue-600 hover:underline">Riprova</button>
          </div>
        ) : isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : clients.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <User className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Nessun cliente trovato</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contatti
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dati Fiscali
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Indirizzo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Canale
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Portale
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {clients.map((client) => (
                <tr key={client.id} onClick={() => openDetail(client.id)} tabIndex={0} aria-label={`Apri ${client.ragione_sociale || client.nome}`}
                  onKeyDown={e => { if (e.target === e.currentTarget && e.key === 'Enter') openDetail(client.id); }} className="hover:bg-gray-50 focus:bg-blue-50 cursor-pointer">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        {client.tipo === 'azienda' ? (
                          <Building2 className="h-8 w-8 text-blue-500" />
                        ) : (
                          <User className="h-8 w-8 text-gray-400" />
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {client.tipo === 'azienda'
                            ? client.ragione_sociale
                            : `${client.nome} ${client.cognome || ''}`}
                        </div>
                        <div className="text-sm text-gray-500">
                          {client.tipo === 'azienda' ? 'Azienda' : 'Persona Fisica'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <CrmContacts email={client.email} telefono={client.telefono} />
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 space-y-1">
                      {client.partita_iva && <div>P.IVA: {client.partita_iva}</div>}
                      {client.codice_fiscale && <div>C.F.: {client.codice_fiscale}</div>}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {client.citta && (
                      <div className="text-sm text-gray-900 flex items-center gap-1">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        {client.citta}
                        {client.provincia && ` (${client.provincia})`}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {client.canali && client.canali.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {client.canali.map((c) => (
                          <span
                            key={c.id}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                            style={{ backgroundColor: c.color, color: contrastColor(c.color) }}
                          >
                            {c.nome}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      {client.has_portal_access ? (
                        client.portal_user_active === false ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            <Globe className="h-3 w-3" />
                            Sospeso
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            <Globe className="h-3 w-3" />
                            Attivo
                          </span>
                        )
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                          <Globe className="h-3 w-3" />
                          No
                        </span>
                      )}
                      {client.agente_nome && (
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <UserCog className="h-3 w-3" />
                          {client.agente_nome}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        <CrmPagination pagination={pagination} label="clienti" onPage={page => updateParam({ page: String(page) })} />
      </div>

    </div>
  );
}
